import React from 'react';
import { Modal, ModalFooter } from '@/components/common';
import { CreateGroupRequest, InstitutionDTO } from '@/models';

interface GroupFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateGroupRequest;
  institutions: InstitutionDTO[];
  isSaving: boolean;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const GroupFormModal: React.FC<GroupFormModalProps> = ({
  isOpen,
  mode,
  formData,
  institutions,
  isSaving,
  onInputChange,
  onSubmit,
  onClose,
}) => {
  const getInstitutionName = (institutionId: string) => {
    const institution = institutions.find((i) => i.id === institutionId);
    return institution?.name || '-';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title={mode === 'create' ? 'Add New Group' : 'Edit Group'}
      size="lg"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmText={mode === 'create' ? 'Create' : 'Update'}
          isLoading={isSaving}
          disabled={!formData.name.trim() || !formData.institutionId}
        />
      }
    >
      <form>
        <div className="row g-3">
          {/* Institution Dropdown */}
          <div className="col-12">
            <label htmlFor="institutionId" className="form-label">
              Institution <span className="text-danger">*</span>
            </label>
            {mode === 'edit' ? (
              <input
                type="text"
                className="form-control"
                value={getInstitutionName(formData.institutionId || '')}
                disabled
              />
            ) : (
              <select
                className="form-select"
                id="institutionId"
                name="institutionId"
                value={formData.institutionId}
                onChange={onInputChange}
                required
              >
                <option value="">Select an institution...</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Basic Info */}
          <div className="col-12">
            <label htmlFor="name" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              maxLength={200}
              required
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="contact" className="form-label">
              Contact Person
            </label>
            <input
              type="text"
              className="form-control"
              id="contact"
              name="contact"
              value={formData.contact || ''}
              onChange={onInputChange}
              maxLength={200}
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="accountNumber" className="form-label">
              Account Number
            </label>
            <input
              type="text"
              className="form-control"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          {/* Address */}
          <div className="col-12">
            <label htmlFor="address" className="form-label">
              Address
            </label>
            <input
              type="text"
              className="form-control"
              id="address"
              name="address"
              value={formData.address || ''}
              onChange={onInputChange}
              maxLength={500}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="city" className="form-label">
              City
            </label>
            <input
              type="text"
              className="form-control"
              id="city"
              name="city"
              value={formData.city || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="province" className="form-label">
              Province
            </label>
            <input
              type="text"
              className="form-control"
              id="province"
              name="province"
              value={formData.province || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="postalCode" className="form-label">
              Postal Code
            </label>
            <input
              type="text"
              className="form-control"
              id="postalCode"
              name="postalCode"
              value={formData.postalCode || ''}
              onChange={onInputChange}
              maxLength={20}
            />
          </div>

          {/* Contact Info */}
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={onInputChange}
              maxLength={200}
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              className="form-control"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={onInputChange}
              maxLength={50}
            />
          </div>

          {/* Additional */}
          <div className="col-12">
            <label htmlFor="safetyVideoUrl" className="form-label">
              Safety Video URL
            </label>
            <input
              type="url"
              className="form-control"
              id="safetyVideoUrl"
              name="safetyVideoUrl"
              value={formData.safetyVideoUrl || ''}
              onChange={onInputChange}
              maxLength={500}
            />
          </div>

          <div className="col-12">
            <label htmlFor="emergencyContactMessage" className="form-label">
              Emergency Contact Message
            </label>
            <textarea
              className="form-control"
              id="emergencyContactMessage"
              name="emergencyContactMessage"
              rows={3}
              value={formData.emergencyContactMessage || ''}
              onChange={onInputChange}
              maxLength={1000}
            />
          </div>

          <div className="col-12">
            <label htmlFor="certificationConfiguration" className="form-label">
              Certification Configuration
            </label>
            <textarea
              className="form-control"
              id="certificationConfiguration"
              name="certificationConfiguration"
              rows={3}
              value={formData.certificationConfiguration || ''}
              onChange={onInputChange}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
